/**
 * What validation said, in the shape that names the fix (LAR-1).
 *
 * **Three outcomes, not two.** A portfolio can fail in two unrelated ways, and the old page was
 * right to headline them apart:
 *
 * * the document is not a portfolio, or is malformed → edit the JSON;
 * * the document is fine and a sleeve is missing → write that sleeve, then revalidate;
 * * everything resolved → create it.
 *
 * `structurally_valid` and `all_deps_resolved` are separate fields on the response for exactly this
 * reason. Collapsing them into one boolean would send an author to the wrong fix roughly half the
 * time, which is the same argument decision SDO-D2 makes about export refusals.
 *
 * The extracted metadata is shown alongside, because it is the author's confirmation that the
 * definition they pasted is the strategy they meant — the name and description are read out of the
 * document, not typed.
 */
import { AlertCircle, AlertTriangle, CheckCircle2 } from 'lucide-react';
import type { DslValidation, PortfolioValidation } from '../labStrategyApi';
import { DependencyList } from './DependencyList';

type Tone = 'ok' | 'warn' | 'bad';

const TONES: Record<Tone, string> = {
  ok: 'border-emerald-200 bg-emerald-50 text-emerald-900',
  warn: 'border-amber-200 bg-amber-50 text-amber-900',
  bad: 'border-red-200 bg-red-50 text-red-800',
};

function Icon({ tone }: { tone: Tone }) {
  if (tone === 'ok') return <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" />;
  if (tone === 'warn') return <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />;
  return <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />;
}

function Metadata({
  name,
  description,
  strategyId,
}: {
  name: string | null;
  description: string | null;
  /** The id read out of the definition — see `DslValidation.symphony_id`. */
  strategyId?: string | null;
}) {
  if (!name && !description && !strategyId) return null;
  return (
    <dl className="mt-2 space-y-0.5 text-xs">
      {strategyId && (
        <div className="flex gap-1.5">
          <dt className="font-semibold">ID</dt>
          <dd className="font-mono break-all">{strategyId}</dd>
        </div>
      )}
      {name && (
        <div className="flex gap-1.5">
          <dt className="font-semibold">Name</dt>
          <dd>{name}</dd>
        </div>
      )}
      {description && (
        <div className="flex gap-1.5">
          <dt className="font-semibold">Description</dt>
          <dd>{description}</dd>
        </div>
      )}
    </dl>
  );
}

function Shell({ tone, headline, children }: { tone: Tone; headline: string; children?: React.ReactNode }) {
  return (
    <div role="status" className={`rounded-lg border px-3 py-2 text-sm ${TONES[tone]}`}>
      <p className="flex items-center gap-2 font-semibold">
        <Icon tone={tone} />
        {headline}
      </p>
      {children}
    </div>
  );
}

export function DslValidationPanel({ result }: { result: DslValidation }) {
  if (!result.valid) {
    return (
      <Shell tone="bad" headline="Does not parse">
        <p className="mt-1 text-xs">{result.error}</p>
      </Shell>
    );
  }
  return (
    <Shell tone="ok" headline="Valid — strategy parsed">
      <Metadata
        name={result.name}
        description={result.description}
        strategyId={result.symphony_id}
      />
    </Shell>
  );
}

export function PortfolioValidationPanel({ result }: { result: PortfolioValidation }) {
  const tone: Tone = result.valid ? 'ok' : result.structurally_valid ? 'warn' : 'bad';
  const headline = result.valid
    ? 'Valid — every sleeve resolved'
    : result.structurally_valid
      ? 'Sleeves unresolved — the document itself is fine'
      : 'Not a valid portfolio document';

  return (
    <Shell tone={tone} headline={headline}>
      {result.structurally_valid && (
        <Metadata
          name={result.name}
          description={result.description}
          strategyId={result.portfolio_id}
        />
      )}
      {result.errors.length > 0 && (
        <ul className="mt-2 space-y-1 text-xs">
          {result.errors.map((message) => (
            <li key={message}>• {message}</li>
          ))}
        </ul>
      )}
      {result.dependencies.length > 0 && (
        <div className="mt-2">
          <DependencyList dependencies={result.dependencies} />
        </div>
      )}
    </Shell>
  );
}
